/**
 * DEV MODE AUTH BYPASS
 * 
 * To enable dev mode (auto-login without manual authentication):
 * Set VITE_DEV_AUTH_BYPASS=true in your .env file
 * 
 * Optional: Set custom dev credentials:
 * VITE_DEV_USER_EMAIL=your-email@test.com
 * VITE_DEV_USER_PASSWORD=your-password
 * 
 * WARNING: Never enable in production!
 */

import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackLogin } from '@/lib/activityTracker';

// Dev mode configuration - set VITE_DEV_AUTH_BYPASS=true in .env to enable
// SECURITY: Dev mode is blocked on production domains regardless of env var
const PRODUCTION_DOMAINS = ['lovable.app', 'lovable.dev', 'atlasdraft.com'];
const isProductionDomain = PRODUCTION_DOMAINS.some(domain => 
  window.location.hostname.endsWith(domain) || window.location.hostname === domain
);
const DEV_MODE_ENABLED = !isProductionDomain && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

// Dev credentials - MUST be provided via env vars, no defaults
const DEV_EMAIL = import.meta.env.VITE_DEV_USER_EMAIL;
const DEV_PASSWORD = import.meta.env.VITE_DEV_USER_PASSWORD;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isDevMode: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const devLoginAttempted = useRef(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Dev mode auto-login effect
  useEffect(() => {
    if (!DEV_MODE_ENABLED || devLoginAttempted.current || loading) return;
    if (user) return; // Already logged in
    
    // Require both email and password from env vars
    if (!DEV_EMAIL || !DEV_PASSWORD) {
      console.warn('🔧 Dev Mode: VITE_DEV_USER_EMAIL and VITE_DEV_USER_PASSWORD must be set');
      return;
    }
    
    devLoginAttempted.current = true;
    console.log('🔧 Dev Mode: Attempting auto-login...');
    
    const autoLogin = async () => {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: DEV_EMAIL,
        password: DEV_PASSWORD,
      });
      
      if (signInError) {
        console.log('🔧 Dev Mode: Creating dev user...');
        const { error: signUpError } = await supabase.auth.signUp({
          email: DEV_EMAIL,
          password: DEV_PASSWORD,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: { display_name: 'Dev User' },
          },
        });
        
        if (signUpError) {
          console.error('🔧 Dev Mode: Failed to create dev user:', signUpError.message);
        } else {
          console.log('🔧 Dev Mode: Dev user created, logging in...');
          await supabase.auth.signInWithPassword({
            email: DEV_EMAIL,
            password: DEV_PASSWORD,
          });
        }
      } else {
        console.log('🔧 Dev Mode: Auto-login successful');
      }
    };
    
    autoLogin();
  }, [loading, user]);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName || email.split('@')[0],
        },
      },
    });
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error) {
      // Track successful login
      trackLogin();
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    return { error: error as Error | null };
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isDevMode: DEV_MODE_ENABLED,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
